import React from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '../../../components/ui/card';
import { CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

/**
 * MedicalCoverageSummaryCard Component
 * Displays medical coverage information for a specific indication
 */
export const MedicalCoverageSummaryCard = ({ medicalCoverageData, drug: selectedDrug }) => {
  if (!medicalCoverageData) return null;

  const { drug: medicalDrug, plan, indication, coverage, formulary } = medicalCoverageData;
  // Use the selected drug from dropdown if available, otherwise fall back to medical coverage drug
  const drug = selectedDrug || medicalDrug;

  /**
   * Visual status helpers
   */
  const getStatusIcon = () => {
    if (coverage.status === 'not_covered') return XCircle;
    if (coverage.paRequired || coverage.stRequired) return AlertCircle;
    return CheckCircle2;
  };

  const getStatusText = () => {
    if (coverage.status === 'not_covered') {
      return 'Not Covered';
    }
    
    if (coverage.paRequired && coverage.stRequired) {
      return 'Covered - Restrictions Apply';
    }
    if (coverage.paRequired) {
      return 'Covered - PA Required';
    }
    if (coverage.stRequired) {
      return 'Covered - Step Therapy Required';
    }
    
    return 'Covered';
  };

  const StatusIcon = getStatusIcon();

  /**
   * Convert hex color to CSS format
   */
  const getTierColor = () => {
    if (!coverage.medicalUnifiedTierColor) return undefined;
    const color = coverage.medicalUnifiedTierColor.startsWith('#') 
      ? coverage.medicalUnifiedTierColor 
      : `#${coverage.medicalUnifiedTierColor}`;
    return color;
  };

  /**
   * Data field component for consistent styling
   */
  const DataField = ({ label, value }) => (
    <div className="space-y-1.5 sm:space-y-2">
      <div className="text-[10px] sm:text-xs font-semibold tracking-wide sm:tracking-wider text-foreground">{label}</div>
      <div className="text-sm sm:text-base font-normal text-foreground">{value || '—'}</div>
    </div>
  );

  return (
    <Card className="border-2 shadow-sm">
      <CardHeader className="pb-2 px-4 sm:px-6">
        <CardTitle className="text-lg sm:text-xl font-semibold tracking-tight">
          Medical Coverage Summary
        </CardTitle>
      </CardHeader>

      <CardContent className="p-4 sm:p-6 md:p-8 space-y-3 sm:space-y-4">

        {/* Primary Status Banner */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 rounded-lg border transition-all">
          <StatusIcon 
            className={`h-5 w-5 sm:h-6 sm:w-6 shrink-0 ${
              coverage.status === 'not_covered' 
                ? 'text-status-not-covered' 
                : (coverage.paRequired || coverage.stRequired) 
                  ? 'text-status-restricted' 
                  : 'text-status-covered'
            }`} 
            strokeWidth={2.5} 
          />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold tracking-wider text-foreground mb-2">
              Coverage Status
            </div>
            <div className={`text-base sm:text-lg font-bold ${
              coverage.status === 'not_covered' 
                ? 'text-status-not-covered' 
                : (coverage.paRequired || coverage.stRequired) 
                  ? 'text-status-restricted' 
                  : 'text-status-covered'
            }`}>
              {getStatusText()}
            </div>
          </div>
          <div className="w-full sm:w-auto text-left sm:text-right pt-3 sm:pt-0 sm:pl-4 border-t sm:border-t-0 sm:border-l">
            <div className="text-xs font-semibold tracking-wider text-foreground">
              Medical Unified Tier
            </div>
            <div className="flex items-center gap-2 mt-1">
              {getTierColor() && (
                <div 
                  className="w-3 h-3 rounded-full border" 
                  style={{ backgroundColor: getTierColor() }}
                />
              )}
              <div className="text-xs font-normal text-foreground">
                {coverage.unifiedTierName}
              </div>
            </div>
          </div>
        </div>

        {/* Two-Column Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 sm:gap-x-8 md:gap-x-10 gap-y-6 sm:gap-y-8">
          {/* Left Column: Plan Information */}
          <div className="space-y-6">
            <DataField label="Plan Type" value={plan.planType} />
            
            <DataField label="Channel" value={plan.channel} />

            <DataField label="Formulary" value={formulary.name} />

            <DataField label="Therapeutic Class" value={drug?.therapeuticClass || drug?.drugClass || '—'} />
          </div>

          {/* Right Column: Utilization Management */}
          <div className="space-y-6">
            <div className="space-y-3 sm:space-y-4">
              {/* Prior Authorization */}
              {coverage.paRequired && (
                <div className="w-full flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg border-2 border-status-restricted/30 bg-status-restricted/5">
                  <div className="mt-0.5 shrink-0">
                    <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-semibold bg-status-restricted text-white border-0">
                      PA
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs sm:text-sm font-semibold text-foreground">
                      Prior Authorization Required
                    </div>
                    <div className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                      Prior authorization is required for this indication.
                    </div>
                  </div>
                </div>
              )}

              {/* Step Therapy */}
              {coverage.stRequired && (
                <div className="w-full flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg border-2 border-status-restricted/30 bg-status-restricted/5">
                  <div className="mt-0.5 shrink-0">
                    <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-semibold bg-status-restricted text-white border-0">
                      ST
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs sm:text-sm font-semibold text-foreground">
                      Step Therapy Required
                    </div>
                    <div className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                      Step therapy requirements apply for this indication.
                    </div>
                  </div>
                </div>
              )}

              {/* Medical Policy */}
              {coverage.medicalPolicy && (
                <div className="w-full flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg border-2 border-status-info/30 bg-status-info/5">
                  <div className="mt-0.5 shrink-0">
                    <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-semibold bg-status-info text-white border-0">
                      Policy
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs sm:text-sm font-semibold text-foreground">
                      Medical Policy Applies
                    </div>
                    <div className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                      This coverage is subject to medical policy guidelines.
                    </div>
                  </div>
                </div>
              )}

              {/* Specialty */}
              {coverage.specialty && (
                <div className="w-full flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg border-2 border-status-info/30 bg-status-info/5">
                  <div className="mt-0.5 shrink-0">
                    <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-semibold bg-status-info text-white border-0">
                      Specialty
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs sm:text-sm font-semibold text-foreground">
                      Specialty Drug
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      This is classified as a specialty medication.
                    </div>
                  </div>
                </div>
              )}

              {/* No Restrictions Listed */}
              {coverage.status === 'covered' && 
               !coverage.paRequired && 
               !coverage.stRequired && (
                <div className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-status-covered/30 bg-status-covered/5">
                  <CheckCircle2 className="h-5 w-5 text-status-covered shrink-0" />
                  <div className="text-sm font-semibold text-foreground">
                    No Restrictions Listed
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end pt-4">
          <span className="text-xs text-muted-foreground">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
